import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import type { InspectionDto, InspectionPhase, InspectionShotKey } from '@carvia/ros-client-types';
import { type MaybeRefOrGetter, computed, toValue } from 'vue';
import { inspectionsService } from '@/api/inspections.service';
import { INSPECTIONS_QUERY_KEY } from '@/utils/evidence-upload-queue';
import { updateItemOnQueriesData } from '@/utils/update-query-data';

export const useBookingInspections = (
  bookingId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [INSPECTIONS_QUERY_KEY, 'booking', computed(() => toValue(bookingId))],
    queryFn: () => inspectionsService.getAllByBookingId(toValue(bookingId)!),
    enabled: computed(() => !!toValue(bookingId) && toValue(enabled)),
  });
};

/** Starts the inspection on first entry and resumes it afterwards (the request is idempotent). */
export const useStartedInspection = (
  allocationId: MaybeRefOrGetter<string | undefined>,
  phase: InspectionPhase,
) => {
  return useQuery({
    queryKey: [INSPECTIONS_QUERY_KEY, 'allocation', computed(() => toValue(allocationId)), phase],
    queryFn: () => inspectionsService.start(toValue(allocationId)!, phase),
    enabled: computed(() => !!toValue(allocationId)),
  });
};

const useInspectionMutation = <V>(mutationFn: (variables: V) => Promise<InspectionDto>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: (inspection) => {
      updateItemOnQueriesData<InspectionDto>([INSPECTIONS_QUERY_KEY], inspection, queryClient);
    },
  });
};

export const useRegisterInspectionCapture = () =>
  useInspectionMutation(
    ({ id, shotKey, captureId }: { id: string; shotKey: InspectionShotKey; captureId: string }) =>
      inspectionsService.registerCapture(id, shotKey, captureId),
  );

export const useSkipInspectionShot = () =>
  useInspectionMutation(
    ({ id, shotKey, reason }: { id: string; shotKey: InspectionShotKey; reason: string }) =>
      inspectionsService.skip(id, shotKey, reason),
  );

export const useCompleteInspection = () =>
  useInspectionMutation((id: string) => inspectionsService.complete(id));
